import { BaseModel } from '@/models/BaseModel';
import { BaseCollection } from './BaseCollection';
import { type PlanningPermissionApplication } from './PlanningPermissionApplication';
import type { MutationsObject } from '@kp/js-model';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { createModel } from '@kp/js-model';

export class User extends BaseModel {
    // properties
    id: number | null = null;
    username: string | null = null;
    password: string | null = null;
    email: string | null = null;
    ist_admin: boolean | null = null;
    last_login: Dayjs | null = null;
    active_role: string | null = null;
    active_role_stelle_id: number | null = null;
    nachname: string | null = null;
    vorname: string | null = null;
    event_notification_per_mail: boolean | null = null;
    api_user: boolean | null = null;
    country: string | null = null;
    locality: string | null = null;
    street_address: string | null = null;
    postal_code: string | null = null;
    titel: string | null = null;
    salutation: string | null = null;
    mobile_number: string | null = null;
    date_of_birth: Dayjs | null = null;
    strassennummer: string | null = null;

    // NOTE: locale is NOT sent from the backend - kept for future implementation:
    locale: string | null = 'de';

    // View fields:
    nachname_vorname: string | null = null;
    stelle_ids: number[] = [];
    gemeinden_ids: number[] | null = null;
    gemeinden: string | null = null;
    fachstellen_ids: number[] | null = null;
    fachstellen: string | null = null;
    gemeinden_invitations_ids: string | null = null;
    gemeinden_invitations: string | null = null;
    fachstellen_invitations_ids: string | null = null;
    fachstellen_invitations: string | null = null;
    active_role_stelle: string | null = null;
    active_role_stelle_street: string | null = null;
    active_role_stelle_house_number: string | null = null;
    active_role_stelle_swiss_zip_code: string | null = null;
    active_role_stelle_town: string | null = null;
    active_role_stelle_eplan_email_address: string | null = null;
    active_role_stelle_event_notification_per_mail: boolean | null = null;
    count_pg_acls: string | null = null;
    active_user_stelle_personal_event_notification_per_mail: boolean | null = null;

    public mutations(): MutationsObject {
        return {
            last_login: (val) => dayjs(val),
            date_of_birth: (val) => dayjs(val),
            event_notification_per_mail: Boolean,
            ist_admin: Boolean,
            api_user: Boolean
        };
    }

    getClassName(): string {
        return 'User';
    }

    get isAdmin() {
        return this.ist_admin;
    }

    get isRoleAdmin() {
        return this.ist_admin && this.active_role === 'admin';
    }

    get isRoleFachstelle() {
        return this.fachstellen_ids && this.fachstellen_ids.length > 0 && this.active_role === 'fachstelle';
    }

    get isRoleGemeinde() {
        return this.gemeinden_ids && this.gemeinden_ids.length > 0 && this.active_role === 'gemeinde';
    }

    get roles() {
        const roles = ['user'];
        if (this.fachstellen_ids && this.fachstellen_ids.length > 0) {
            roles.push('fachstelle');
        }
        if (this.gemeinden_ids && this.gemeinden_ids.length > 0) {
            roles.push('gemeinde');
        }
        if (this.isAdmin) {
            roles.push('admin');
        }

        return roles;
    }

    isInGemeinde(gemeinde_id: number | null) {
        return gemeinde_id && this.gemeinden_ids && this.gemeinden_ids.indexOf(gemeinde_id) >= 0;
    }

    isInFachstelle(stelle_id: number | null) {
        return stelle_id && this.fachstellen_ids && this.fachstellen_ids.indexOf(stelle_id) >= 0;
    }

    isInStelle(stelle_id: number | null) {
        return this.isInGemeinde(stelle_id) || this.isInFachstelle(stelle_id);
    }

    isInAtLeastOneStelle(stelle_ids: number[] | null) {
        if (!stelle_ids || stelle_ids.length === 0) {
            return false;
        }
        stelle_ids = stelle_ids || [-1];
        for (const id of stelle_ids) {
            if (this.isInStelle(id)) {
                return true;
            }
        }
        return false;
    }

    get name() {
        const name = [];
        if (this.vorname) {
            name.push(this.vorname);
        }
        if (this.nachname) {
            name.push(this.nachname);
        }
        if (name.length === 0) {
            return this.username;
        }
        return name.join(' ');
    }

    get initialen() {
        const initialen = [];
        if (this.vorname) {
            initialen.push(this.vorname[0].toUpperCase());
        }
        if (this.nachname) {
            initialen.push(this.nachname[0].toUpperCase());
        }
        if (initialen.length === 0) {
            return this.username ? this.username[0].toUpperCase() : '';
        }
        return initialen.join('');
    }

    isWriterOfGesuch(gesuch: PlanningPermissionApplication | null): boolean {
        if (!gesuch) {
            return false;
        }

        if (this.isRoleAdmin) {
            return true;
        }

        if (this.isRoleGemeinde) {
            let gems = gesuch.write_gemeinden ? gesuch.write_gemeinden : [];
            for (let i = 0; i < gems.length; i++) {
                if (this.isInGemeinde(gems[i])) {
                    return true;
                }
            }
            return false;
        }

        return (gesuch.writers || []).indexOf(this.id ? this.id : -1) >= 0;
    }
}

export async function createNewUser(): Promise<User> {
    const user = createModel(User, {});

    await user.save();
    return user;
}

export class UserCollection extends BaseCollection<User> {
    protected modelCls = User;

    public getModelClassName(): string {
        return 'User';
    }
}
